import { Route, Routes } from 'react-router-dom'
import NotFound from 'routes/NotFound'
import Page from 'components/global/Page'
import { useApp } from 'store/steps'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import ServerErrorModal from 'components/global/ServerErrorModal'
import { useUnlimited } from 'store/unlimited-slice/hooks'
import { setIsErrorModalOpened } from 'store/general-slice'
import { UNLIMITED_RENEW_STEPS } from 'routes/steps'
import CardOrder from 'routes/unlimited-card/CardOrder'
import UnlimitedCalendarModal from 'components/global/CalendarModal/forks/UnlimitedCalendarModal'
import {
    setIsCalendarModalOpened,
    setIsRenewProcessVerified,
} from 'store/unlimited-slice'
import RenewVerification from 'routes/unlimited-card/RenewVerification'
import RenewSummary from 'routes/unlimited-card/RenewSummary'
import {
    useGetCartQuery,
    useGetOrderCardRenewTokenQuery,
} from 'store/api-slice/hooks'
import FullPageLoader from 'components/global/FullPageLoader'
import { useSearchParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { HOME_LINK } from 'constants/links'
import { setIsFetchingLocked } from 'store/cart-slice'
import { unlimitedRenewFirstStep } from 'routes/basenames'
import ConfirmationModal from 'components/global/ConfirmationModal'
import { DEFAULT_LANG } from 'constants/i18n'

const UnlimitedRenew = () => {
    const dispatch = useAppDispatch()
    const [searchParams] = useSearchParams()
    const { getPath, language, navigateToStep } = useApp()
    const { removeCardsFromCart, isProcessing } = useUnlimited()

    const { isRenewProcessVerified, isCalendarModalOpened } = useAppSelector(
        (state) => state.unlimited
    )

    const [isCartChecked, setIsCartChecked] = useState(false)
    const [isTokenChecked, setIsTokenChecked] = useState(false)
    const [isTokenCheckLocked, setIsTokenCheckLocked] = useState(true)

    const tokenUrlParam = searchParams.get('token')

    const { data: cartData, isFetching: isCartFetching } = useGetCartQuery(
        {
            _locale: language ?? DEFAULT_LANG,
            cartCreatorType: 'card-renew',
        },
        {
            skip: isCartChecked,
        }
    )

    const {
        data: tokenVerificationData,
        isFetching: isTokenVerificationFetching,
        isError: isTokenVerificationError,
    } = useGetOrderCardRenewTokenQuery(
        {
            _locale: language ?? DEFAULT_LANG,
            orderCardRenewTokenId: tokenUrlParam || '',
        },
        {
            skip: isTokenCheckLocked || isTokenChecked,
        }
    )

    const isToken = !!tokenUrlParam
    const isSession = isCartChecked && !!cartData

    const handleAcceptError = () => {
        removeCardsFromCart(true).then(() => {
            dispatch(setIsErrorModalOpened(false))
            window.location.replace(unlimitedRenewFirstStep)
        })
    }

    const handleCloseCalendar = () => {
        dispatch(setIsCalendarModalOpened(false))
    }

    useEffect(() => {
        // wait for cart/session or token being checked
        if (isCartFetching || isTokenVerificationFetching) return

        // check cart only once
        if (!isCartChecked) {
            setIsCartChecked(true)
            return
        }

        // verify url token only once
        if (
            (isTokenVerificationError || tokenVerificationData) &&
            !isTokenChecked
        ) {
            setIsTokenChecked(true)
            return
        }

        // if no token in url...
        if (!isToken) {
            // ...but session created and card verified
            if (isSession || isRenewProcessVerified) {
                // ...redirect to summary when reloading page with session
                if (!isRenewProcessVerified) {
                    dispatch(setIsRenewProcessVerified(true))
                    navigateToStep({ stepId: UNLIMITED_RENEW_STEPS[1].id })
                }
            } else {
                // ...and when no session - redirect to homepage
                location.replace(HOME_LINK)
            }
        }

        // if token in url present...
        if (isToken) {
            // ...and there is no session
            if (!isSession) {
                if (isTokenCheckLocked) {
                    // ...unlock query and verify token
                    setIsTokenCheckLocked(false)
                } else if (isTokenChecked) {
                    // ...or redirect to homepage when token is incorrect
                    if (isTokenVerificationError || !tokenVerificationData) {
                        location.replace(HOME_LINK)
                    }
                }
            } else {
                // if there is session - remove one and reload page
                removeCardsFromCart(false, true).then(() => {
                    window.location.href = `${window.location.pathname}?token=${tokenUrlParam}`
                })
            }
        }
    }, [
        isCartChecked,
        isCartFetching,
        cartData,
        tokenUrlParam,
        isTokenVerificationFetching,
        isTokenVerificationError,
    ])

    // remove token from url after positive verification
    useEffect(() => {
        // ...but skip when trying to run new renewal on existing session
        if ((isToken && isSession) || !tokenVerificationData) return
        window.history.pushState({}, document.title, window.location.pathname)
    }, [tokenVerificationData, isToken, isSession])

    useEffect(() => {
        dispatch(setIsFetchingLocked(false))
    }, [])

    return (!isCartChecked || !isTokenChecked || !tokenVerificationData) &&
        !isRenewProcessVerified ? (
        <FullPageLoader />
    ) : (
        <Page>
            <Routes>
                <Route
                    path="/"
                    element={<RenewVerification {...tokenVerificationData} />}
                />
                <Route
                    path={getPath('renew_summary')}
                    element={<RenewSummary />}
                />
                <Route path={getPath('renew_order')} element={<CardOrder />} />
                <Route path="*" element={<NotFound />} />
            </Routes>
            <UnlimitedCalendarModal
                isOpened={isCalendarModalOpened}
                onClose={handleCloseCalendar}
            />
            <ServerErrorModal
                onClick={handleAcceptError}
                isLoading={isProcessing}
            />
            <ConfirmationModal />
        </Page>
    )
}

export default UnlimitedRenew
